import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useChineseLang } from '../../i18n/chinese'
import { AppUseSelector } from '../../redux/hooks'
import { setAudioRecording } from '../../redux/actions/recorderAction'
import Recorder from 'containers/recorder/Recorder'
import Loader from 'containers/loader/loader'
import OneLineContent from 'containers/RenderStroyComponent/OneLineContent'
import { answerValidate } from 'utils/utils'
import { SpeakingMode, TasterWordInfo } from 'types/tasterTypes'
import './writing-speaking-submission.css'

export type { SpeakingMode }

export interface WritingSpeakingPayload {
  answer?: string
  recordingFile?: File
  speakingMode?: SpeakingMode
}

export interface WritingSpeakingSubmissionProps {
  /** The writing prompt, displayed prominently above the form. */
  question: string
  /** First sentence of the story — shown when the "Read the first sentence" mode is picked. */
  firstSentence?: string
  /** Segmented question words (same shape as GET /question's questionContent). When present, the question renders tappable via OneLineContent. */
  questionContent?: TasterWordInfo[]
  /** Segmented first-sentence words — tappable rendering for the 'read_sentence' mode. */
  firstSentenceContent?: TasterWordInfo[]
  /** Word tap handler, (event, wordObj, sentence, key) — the OneLineContent/Word contract. Provided by the page that owns the WordDetail popup. */
  handleClickWord?: (event: any, word: any, sentence: any, key: string) => void
  /** Active word key for the popup highlight (owned by the same page). */
  activeIndex?: string
  /** Placeholder for the writing textarea. */
  writingPlaceholder?: string
  /** Label on the submit button. */
  submitLabel?: string
  /** Parent-controlled busy state (upload + submit round-trip). Disables the form. */
  isSubmitting?: boolean
  /** Parent-level submission error (e.g. the backend rejected the submit). */
  errorMessage?: string
  /**
   * Called once local validation passes. At least one of answer / recordingFile
   * is present; speakingMode is set whenever a recording is.
   */
  onSubmit: (payload: WritingSpeakingPayload) => void | Promise<void>
}

// label and hint are translation keys.
const SPEAKING_MODES: Array<{ value: SpeakingMode; label: string; hint: string }> = [
  {
    value: 'read_writing',
    label: 'writeSpeak:submission.modes.read_writing.label',
    hint: 'writeSpeak:submission.modes.read_writing.hint',
  },
  {
    value: 'free_talk',
    label: 'writeSpeak:submission.modes.free_talk.label',
    hint: 'writeSpeak:submission.modes.free_talk.hint',
  },
  {
    value: 'read_sentence',
    label: 'writeSpeak:submission.modes.read_sentence.label',
    hint: 'writeSpeak:submission.modes.read_sentence.hint',
  },
]

/**
 * Reusable writing + speaking submission form: a prompt, a Chinese-writing
 * textarea, and ONE recording made in one of three speaking modes. First used
 * by the teacher-feedback taster (/taster); designed to later replace the
 * in-story writing section, so it owns only form state and local validation —
 * uploading and submitting stay with the consumer via onSubmit.
 */
const WritingSpeakingSubmission: React.FC<WritingSpeakingSubmissionProps> = ({
  question,
  firstSentence,
  questionContent,
  firstSentenceContent,
  handleClickWord,
  activeIndex = '-1',
  writingPlaceholder,
  submitLabel,
  isSubmitting = false,
  errorMessage = '',
  onSubmit,
}) => {
  const { t } = useTranslation('writeSpeak')
  const zhLang = useChineseLang()
  const currentAudioRecording = AppUseSelector(({ recorder }: any) => recorder.currentAudioRecording)
  const { chineseWordFontSize } = AppUseSelector(({ dashboard }: any) => dashboard)
  const [answer, setAnswer] = useState('')
  const [speakingMode, setSpeakingMode] = useState<SpeakingMode>('read_writing')
  const [validationError, setValidationError] = useState('')

  // A stale recording from another page (story listening, dashboard) must not
  // leak into this submission — the recorder state is app-global.
  useEffect(() => {
    setAudioRecording({})
  }, [])

  const hasRecording = Boolean(currentAudioRecording?.size)
  const activeMode = SPEAKING_MODES.find((m) => m.value === speakingMode) || SPEAKING_MODES[0]

  const handleSubmit = async () => {
    if (isSubmitting) return

    const trimmedAnswer = answer.trim()
    if (!trimmedAnswer && !hasRecording) {
      setValidationError(t('submission.nothingToSend'))
      return
    }
    if (trimmedAnswer) {
      const answerHasError = await answerValidate(trimmedAnswer)
      if (answerHasError?.error?.length) {
        setValidationError(answerHasError.error)
        return
      }
    }
    setValidationError('')

    await onSubmit({
      ...(trimmedAnswer ? { answer: trimmedAnswer.replace(/[\n\r]/g, '') } : {}),
      ...(hasRecording ? { recordingFile: currentAudioRecording as File, speakingMode } : {}),
    })
  }

  const displayError = validationError || errorMessage

  // One-tap dictionary wherever segmented words + a tap handler exist; plain text otherwise.
  const questionWords = questionContent || []
  const sentenceWords = firstSentenceContent || []
  const questionIsTappable = Boolean(questionWords.length && handleClickWord)
  const sentenceIsTappable = Boolean(sentenceWords.length && handleClickWord)

  return (
    <div className='wss'>
      {/* The story's writing question — a plain labeled block, no card. Same
          tappable rendering as the story question page (OneLineContent). */}
      <div className='wss-question'>
        <div className='box-title mb-10'>{t('submission.todaysQuestion')}</div>
        {questionIsTappable ? (
          <div className='word_wrapper'>
            {questionWords.map((wordObj, index) => (
              <OneLineContent
                key={index}
                WIndex={index}
                iterator={'WSS-QUESTION'}
                isFontBold={true}
                chineseWordFontSize={chineseWordFontSize}
                wordObj={wordObj}
                contentDetail={questionWords}
                story={{}}
                showPinYin={true}
                activeIndex={activeIndex}
                handleClickWord={handleClickWord}
              />
            ))}
          </div>
        ) : (
          <p className='wss-question-text' lang={zhLang}>
            {question}
          </p>
        )}
      </div>

      {/* Writing */}
      <div className='wss-section'>
        <div className='wss-section-head'>
          <span className='wss-section-step'>1</span>
          <div>
            <h4 className='wss-section-title'>{t('submission.writeTitle')}</h4>
            <p className='wss-section-sub'>{t('submission.writeSub')}</p>
          </div>
        </div>
        <div className='question-textarea wss-textarea'>
          <textarea
            placeholder={writingPlaceholder ?? t('submission.writingPlaceholder')}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={isSubmitting}
          />
        </div>
      </div>

      {/* Speaking */}
      <div className='wss-section'>
        <div className='wss-section-head'>
          <span className='wss-section-step'>2</span>
          <div>
            <h4 className='wss-section-title'>{t('submission.speakTitle')}</h4>
            <p className='wss-section-sub'>{t('submission.speakSub')}</p>
          </div>
        </div>

        <div className='wss-modes' role='radiogroup' aria-label={t('submission.modesLabel')}>
          {SPEAKING_MODES.map((mode) => (
            <button
              key={mode.value}
              type='button'
              role='radio'
              aria-checked={speakingMode === mode.value}
              className={`wss-mode-btn${speakingMode === mode.value ? ' active' : ''}`}
              onClick={() => setSpeakingMode(mode.value)}
              disabled={isSubmitting}
            >
              {t(mode.label)}
            </button>
          ))}
        </div>

        <p className='wss-mode-hint'>{t(activeMode.hint)}</p>
        {speakingMode === 'read_sentence' &&
          (sentenceIsTappable ? (
            <div className='wss-first-sentence'>
              <div className='word_wrapper'>
                {sentenceWords.map((wordObj, index) => (
                  <OneLineContent
                    key={index}
                    WIndex={index}
                    iterator={'WSS-FIRST-SENTENCE'}
                    chineseWordFontSize={chineseWordFontSize}
                    wordObj={wordObj}
                    contentDetail={sentenceWords}
                    story={{}}
                    showPinYin={true}
                    activeIndex={activeIndex}
                    handleClickWord={handleClickWord}
                  />
                ))}
              </div>
            </div>
          ) : firstSentence ? (
            <p className='wss-first-sentence' lang={zhLang}>
              {firstSentence}
            </p>
          ) : null)}

        <div className='wss-recorder'>
          <Recorder />
        </div>
      </div>

      {displayError !== '' && (
        <div className='error wss-error'>
          <i className='fa fa-exclamation-circle' /> {displayError}
        </div>
      )}

      <div className='wss-footer'>
        <button
          type='button'
          className='button-2 w-button green-btn-class wss-submit'
          disabled={isSubmitting || (!answer.trim() && !hasRecording)}
          onClick={handleSubmit}
        >
          {isSubmitting ? <Loader showText={false} /> : (submitLabel ?? t('submission.submit'))}
        </button>
      </div>
    </div>
  )
}

export default WritingSpeakingSubmission
